import axios from 'axios';
import type { Pro, ProService, Forfait, CreditPack, Event, Profession } from '../../types';
import { getApiUrl, buildApiUrl } from '../../config/apiConfig';

function optionalAuthHeaders(token?: string | null): Record<string, string> | undefined {
  const trimmed = (token || '').trim();
  if (!trimmed) return undefined;
  return { Authorization: `Bearer ${trimmed}` };
}

// ============ PRO ============
function assertJsonProPayload(
  data: unknown,
  context: string
): asserts data is Record<string, unknown> {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error(
      `${context} : réponse invalide (attendu un objet pro). Souvent le serveur renvoie une page HTML (URL d’API incorrecte).`
    );
  }
}

export async function getProBySlug(slug: string): Promise<Pro> {
  const response = await axios.get(`${getApiUrl('/get-pro-by-slug')}/get-pro-by-slug/${slug}`);
  assertJsonProPayload(response.data, 'getProBySlug');
  return {
    ...(response.data as unknown as Pro),
    is_premium:
      response.data.is_premium === true ||
      response.data.is_premium === 'true' ||
      response.data.is_premium === 1,
  };
}

export async function getProById(proId: number): Promise<Pro> {
  const response = await axios.get(`${getApiUrl('/get-pro')}/get-pro/${proId}`);
  return response.data;
}

// ============ SERVICES ============
export async function getProServices(
  proId: number,
  token?: string | null
): Promise<ProService[]> {
  const response = await axios.get(`${getApiUrl('/get-services')}/get-services/${proId}`, {
    headers: optionalAuthHeaders(token),
  });
  return response.data.services || [];
}

export async function getProfessions(): Promise<Profession[]> {
  const response = await axios.get(`${getApiUrl('/get-professions')}/get-professions`);
  return response.data.professions || [];
}

export async function getProProfessionsOrder(proId: number): Promise<Profession[]> {
  const response = await axios.get(
    `${getApiUrl('/get-pro-professions-order')}/get-pro-professions-order/${proId}`
  );
  return response.data.professions || [];
}

const AVAILABLE_DATES_CACHE_TTL_MS = 30_000;

type AvailableDatesCacheEntry = {
  promise?: Promise<string[]>;
  result?: string[];
  expiresAt?: number;
};

const availableDatesCache = new Map<string, AvailableDatesCacheEntry>();

function availableDatesCacheKey(serviceId: number, params?: Record<string, string | number>): string {
  if (!params || Object.keys(params).length === 0) {
    return String(serviceId);
  }
  const sorted = Object.keys(params)
    .sort()
    .map(k => `${k}=${params[k]}`)
    .join('&');
  return `${serviceId}?${sorted}`;
}

/** Tests uniquement — vide le cache module getAvailableDates. */
export function __resetAvailableDatesCacheForTests(): void {
  availableDatesCache.clear();
}

export async function getAvailableDates(
  serviceId: number,
  params?: Record<string, string | number>
): Promise<string[]> {
  const key = availableDatesCacheKey(serviceId, params);
  const now = Date.now();
  const existing = availableDatesCache.get(key);

  if (existing?.result !== undefined && existing.expiresAt !== undefined && existing.expiresAt > now) {
    return existing.result;
  }
  if (existing?.promise) {
    return existing.promise;
  }

  const promise = (async () => {
    try {
      const response = await axios.get(
        `${getApiUrl('/get-available-dates')}/get-available-dates/${serviceId}`,
        params && Object.keys(params).length > 0 ? { params } : undefined
      );
      const dates: string[] = response.data.availableDates || [];
      const emptyReason = response.data.emptyReason as string | undefined;
      const cacheable = emptyReason !== 'unknown';

      if (cacheable) {
        availableDatesCache.set(key, {
          result: dates,
          expiresAt: Date.now() + AVAILABLE_DATES_CACHE_TTL_MS,
        });
      } else {
        availableDatesCache.delete(key);
      }
      return dates;
    } catch (error) {
      availableDatesCache.delete(key);
      throw error;
    }
  })();

  availableDatesCache.set(key, { promise });

  try {
    return await promise;
  } finally {
    const entry = availableDatesCache.get(key);
    if (entry?.promise === promise) {
      const { result, expiresAt } = entry;
      if (result !== undefined && expiresAt !== undefined) {
        availableDatesCache.set(key, { result, expiresAt });
      } else {
        availableDatesCache.delete(key);
      }
    }
  }
}

export interface AvailableTimesOptions {
  employeeId?: number | null;
  dynamicPauseBeforeExtra?: number;
  dynamicPauseAfterExtra?: number;
}

/** Créneau occupé côté client (ticket #872) — start/end uniquement. */
export interface ClientOccupiedBlock {
  start: string;
  end: string;
}

export interface AvailableTimesResult {
  availableTimes: string[];
  occupiedBlocks: ClientOccupiedBlock[];
}

export async function getAvailableTimes(
  serviceId: number,
  date: string,
  employeeIdOrOptions?: number | null | AvailableTimesOptions
): Promise<AvailableTimesResult> {
  const options: AvailableTimesOptions =
    typeof employeeIdOrOptions === 'object' && employeeIdOrOptions !== null
      ? employeeIdOrOptions
      : { employeeId: employeeIdOrOptions };
  const params: Record<string, string | number> = { service_id: serviceId, date };
  if (options.employeeId != null) {
    params.employee_id = options.employeeId;
  }
  if (options.dynamicPauseBeforeExtra && options.dynamicPauseBeforeExtra > 0) {
    params.dynamic_pause_before_extra = options.dynamicPauseBeforeExtra;
  }
  if (options.dynamicPauseAfterExtra && options.dynamicPauseAfterExtra > 0) {
    params.dynamic_pause_after_extra = options.dynamicPauseAfterExtra;
  }
  const response = await axios.get(
    `${getApiUrl('/get-available-times-improved')}/get-available-times-improved`,
    {
      params,
    }
  );
  const rawBlocks = (response.data.occupiedBlocks || []) as Array<Record<string, unknown>>;
  const occupiedBlocks: ClientOccupiedBlock[] = rawBlocks
    .filter(block => block.start != null && block.end != null)
    .map(block => ({
      start: String(block.start),
      end: String(block.end),
    }));
  return {
    availableTimes: response.data.availableTimes || [],
    occupiedBlocks,
  };
}

/** Employés d'équipe (intervenants) — endpoint public pour affichage client */
export interface TeamEmployee {
  employee_id: number;
  display_name: string;
  color_hex?: string | null;
  is_active?: boolean;
}

export async function getProTeamEmployees(proId: number): Promise<TeamEmployee[]> {
  const response = await axios.get(buildApiUrl(`/teams/${proId}/employees`));
  const list = (response.data?.employees || []) as TeamEmployee[];
  return list.filter(e => e.is_active !== false);
}

export async function getServiceSessions(
  serviceId: number,
  locationId?: number
): Promise<
  {
    creneau_id: number;
    session_date: string;
    start_time: string;
    end_time: string;
    location?: string;
    location_id?: number;
    max_participants: number;
    current_participants: number;
    assigned_employee_name?: string | null;
    assigned_employee_color?: string | null;
  }[]
> {
  const params: Record<string, string | number> = {};
  if (locationId !== undefined && locationId !== null) {
    params.location_id = locationId;
  }

  const response = await axios.get(
    `${getApiUrl('/service-sessions')}/service-sessions/service/${serviceId}`,
    {
      params,
    }
  );
  return response.data.sessions || [];
}

export async function getSessionDetails(creneauId: number): Promise<{
  creneau_id: number;
  session_date: string;
  start_time: string;
  end_time: string;
  location?: string;
  location_id?: number;
  max_participants: number;
  current_participants: number;
}> {
  const response = await axios.get(
    `${getApiUrl('/service-sessions')}/service-sessions/${creneauId}`
  );
  return response.data.session;
}

// ============ FORFAITS ============
export async function getProForfaits(proId: number): Promise<Forfait[]> {
  const response = await axios.get(`${getApiUrl('/forfaits')}/forfaits`, {
    params: { pro_id: proId },
  });
  const allForfaits = response.data.forfaits || [];
  return allForfaits.filter((f: Forfait) => f.status !== 'archived');
}

/** Page publique : GET /pro/:proSlug/forfait/:forfaitSlug (API secondaire / forfaits_bp) */
export async function getForfaitByProSlug(
  proSlug: string,
  forfaitSlug: string
): Promise<(Forfait & Record<string, unknown>) | null> {
  const path = `pro/${encodeURIComponent(proSlug)}/forfait/${encodeURIComponent(forfaitSlug)}`;
  const response = await axios.get(buildApiUrl(path));
  return response.data?.forfait ?? null;
}

/** Aligné sur GET /get-valid-forfaits (user_forfait_id = clé pour use_forfait_id à la réservation). */
export type UserValidForfait = {
  user_forfait_id: number;
  forfait_id: number;
  name: string;
  remaining: number;
};

export async function getUserValidForfaits(
  userId: number,
  serviceId: number,
  token: string
): Promise<UserValidForfait[]> {
  const response = await axios.get(`${getApiUrl('/get-valid-forfaits')}/get-valid-forfaits`, {
    params: { user_id: userId, service_id: serviceId },
    headers: { Authorization: `Bearer ${token}` },
  });
  const forfaits = response.data.forfaits || [];
  return forfaits.filter((f: UserValidForfait) => f.remaining > 0);
}

// ============ CREDITS ============
export async function getCreditPacks(proId: number): Promise<CreditPack[]> {
  const response = await axios.get(`${getApiUrl('/pro')}/pro/${proId}/credit-packs`);
  return response.data.packs || [];
}

/** Page publique : GET /pro/:proSlug/credit-pack/:packSlug (payment_bp → API primaire) */
export async function getCreditPackByProSlug(
  proSlug: string,
  packSlug: string
): Promise<(CreditPack & Record<string, unknown>) | null> {
  const path = `pro/${encodeURIComponent(proSlug)}/credit-pack/${encodeURIComponent(packSlug)}`;
  const response = await axios.get(`${getApiUrl('/purchase-credit-pack')}/${path}`);
  return response.data?.pack ?? null;
}

export async function getUserCredits(
  userId: number,
  proId: number,
  token: string
): Promise<number> {
  const response = await axios.get(`${getApiUrl('/user-credits')}/user-credits/${userId}`, {
    params: { pro_id: proId },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.credits || 0;
}

// ============ EVENTS ============
export async function getProEvents(proId: number, token?: string | null): Promise<Event[]> {
  const response = await axios.get(`${getApiUrl('/pros')}/pros/${proId}/events`, {
    headers: optionalAuthHeaders(token),
  });
  return response.data || [];
}

// ============ CLIENT STATUS ============
export async function checkNewClient(userId: number, proId: number): Promise<boolean> {
  const response = await axios.get(
    `${getApiUrl('/check-new-client')}/check-new-client/${userId}/${proId}`
  );
  return response.data?.is_new_client ?? true;
}

// ============ CGV ============
export async function getCGVInfo(
  proId: number,
  userId?: number
): Promise<{ has_cgv: boolean; cgv_content?: string; version_id?: number }> {
  const params: Record<string, number> = {};
  if (userId) params.user_id = userId;

  const response = await axios.get(`${getApiUrl('/public')}/public/get-pro-cgv/${proId}`, {
    params,
  });
  return response.data;
}

// ============ BOARDING ============

export interface BoardingSettings {
  online_booking_enabled?: boolean;
  max_simultaneous_stays?: number | null;
  /** Ticket #197 — libellé public (ex. Garderie). */
  boarding_client_label?: string | null;
  [key: string]: unknown;
}

export async function getBoardingSettings(proId: number): Promise<BoardingSettings | null> {
  try {
    const response = await axios.get(buildApiUrl(`/boarding/settings/${proId}`));
    return response.data.settings || null;
  } catch {
    return null;
  }
}

export interface ProBoardingPoolApi {
  pool_id: number;
  name: string;
  description?: string;
  default_price?: number;
  time_unit?: string;
  night_allowed?: boolean;
  morning_start?: string;
  morning_end?: string;
  afternoon_start?: string;
  afternoon_end?: string;
  checkin_start?: string;
  checkin_end?: string;
  checkout_start?: string;
  checkout_end?: string;
  [key: string]: unknown;
}

export async function getProPools(proId: number): Promise<ProBoardingPoolApi[]> {
  const response = await axios.get(buildApiUrl(`/boarding/pools/pro/${proId}`));
  return response.data.pools || [];
}

export async function checkPoolAvailability(
  poolId: number,
  startDate: string,
  endDate: string
): Promise<{ available: boolean; total_units: number; max_occupied: number }> {
  const response = await axios.get(buildApiUrl('/boarding/pools/availability'), {
    params: { pool_id: poolId, start_date: startDate, end_date: endDate },
  });
  return response.data;
}

export interface PricingResult {
  total_price_eur: number;
  breakdown: any[];
  nights: number;
  perAnimal?: { animalId: number; total: number; breakdown: any[] }[];
}

export async function estimatePrice(
  proId: number,
  poolId: number,
  startDate: string,
  endDate: string,
  animalIds: number[]
): Promise<PricingResult> {
  let total = 0;
  const allBreakdown: any[] = [];
  const perAnimal: { animalId: number; total: number; breakdown: any[] }[] = [];

  const s = new Date(startDate);
  const e = new Date(endDate);
  const nights = Math.max(0, Math.ceil((e.getTime() - s.getTime()) / (1000 * 3600 * 24)));

  if (animalIds.length === 0) return { total_price_eur: 0, breakdown: [], nights };

  for (let i = 0; i < animalIds.length; i++) {
    const response = await axios.post(buildApiUrl('/boarding/pricing/simulate'), {
      pro_id: proId,
      pool_id: poolId,
      start_date: startDate,
      end_date: endDate,
      animal_id: animalIds[i],
      is_second_animal: i > 0,
    });

    if (response.data) {
      const animalPrice = response.data.total_price;
      total += animalPrice;
      if (response.data.breakdown) {
        allBreakdown.push(...response.data.breakdown);
      }
      perAnimal.push({
        animalId: animalIds[i],
        total: animalPrice,
        breakdown: response.data.breakdown || [],
      });
    }
  }

  return { total_price_eur: total, breakdown: allBreakdown, nights, perAnimal };
}
